import { NavLink } from 'react-router-dom';
import {
    LayoutDashboard,
    Building2,
    Package,
    ClipboardCheck,
    GraduationCap,
    FileText,
    Calculator,
    Flame,
    LogOut
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface NavItem {
    name: string;
    path: string;
    icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Facilities', path: '/facilities', icon: Building2 },
    { name: 'Equipment', path: '/equipment', icon: Package },
    { name: 'Inspections', path: '/inspections', icon: ClipboardCheck },
    { name: 'Training', path: '/training', icon: GraduationCap },
    { name: 'Calculators', path: '/calculators', icon: Calculator },
    { name: 'Reports', path: '/reports', icon: FileText },
];

export default function Sidebar() {
    const { user, logout } = useAuth();

    return (
        <aside className="w-64 bg-gradient-to-b from-charcoal-950 to-charcoal-900 text-white flex flex-col h-screen sticky top-0">
            {/* Logo */}
            <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-ember-500 to-ember-600 rounded-xl flex items-center justify-center shadow-ember">
                        <Flame className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="font-display font-bold text-lg tracking-tight">FireSafe</h1>
                        <p className="text-xs text-steel-400">Compliance Pro</p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${isActive
                                    ? 'bg-gradient-to-r from-ember-500 to-ember-600 text-white shadow-ember'
                                    : 'text-steel-300 hover:bg-white/5 hover:text-white'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${isActive ? 'animate-float' : ''}`} />
                                    <span className="font-medium">{item.name}</span>
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* User Profile */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-safety-500 to-safety-600 rounded-full flex items-center justify-center text-white font-bold">
                        {user?.fullName?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{user?.fullName || 'User'}</p>
                        <p className="text-xs text-steel-400 truncate">{user?.email}</p>
                    </div>
                </div>
                <button
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-steel-300 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200 group"
                >
                    <LogOut className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    <span className="font-medium">Logout</span>
                </button>
            </div>
        </aside>
    );
}
